import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { UserPicker, type UserPickerOption } from '@/components/ui/user-picker';
import {
  resolvedNameLabel,
  useResolveUserDisplayNames,
} from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useUserSearch } from '@/hooks/useUserSearch';
import { isFullIsoDate } from '@/lib/format';
import type { PhysicalChartViewModel } from '@/models/patient';

import { CollapsibleCard, type CollapsibleCardSummaryRow } from '../_shared/CollapsibleCard';
import { DraftFooter } from '../_shared/DraftFooter';

/**
 * Shared physical-chart card (PTV2-1157). Records where a paper chart lives —
 * date + location (a static list where "Staff Member" reveals the tenant-user
 * picker and "Other" a free-text field) + notes. The `PhysicalChart` entity is
 * polymorphic on the BE (`PhysicalChartParentEntityType = {PERSON, FAMILY}`),
 * so the same card serves both parents; the parent-specific save wiring lives
 * in the thin wrappers (`PhysicalChartCard` for PERSON,
 * `family-record/.../PhysicalChartCard` for FAMILY) that own the mutation and
 * pass `chart` / `onSave` here.
 *
 * The card reuses `PhysicalChartViewModel` from the patient model as its
 * canonical shape; the family view model is structurally identical.
 */

/**
 * Interim Phase-1 location set. Phase 2 will replace this with an
 * admin-configurable list (PTV2-1064 contract); for now the values are stored
 * verbatim on the wire, with "Staff Member" the one sentinel that reveals the
 * tenant-user picker. "Other" lets the user type a free-text location without
 * losing the dropdown chrome.
 */
const STAFF_LOCATION = 'Staff Member';
const OTHER_LOCATION = 'Other';
const STATIC_LOCATIONS: readonly string[] = [
  'Front Desk',
  'Records Room',
  'Lab',
  'Off-site Storage',
  STAFF_LOCATION,
  OTHER_LOCATION,
];

const EMPTY_CHART: PhysicalChartViewModel = {
  id: null,
  date: null,
  location: null,
  staffMemberUserGuid: null,
  notes: null,
};

export type PhysicalChartParentType = 'PERSON' | 'FAMILY';

interface PhysicalChartCardSharedProps {
  /** Which parent the chart hangs off — drives the card's stable id/anchor. */
  readonly parentType: PhysicalChartParentType;
  /** Visible heading (caller owns i18n so PERSON/FAMILY can differ). */
  readonly title: string;
  readonly chart: PhysicalChartViewModel | null;
  readonly saving: boolean;
  /** Persist the next chart (or `null` to clear). Caller owns the mutation. */
  readonly onSave: (next: PhysicalChartViewModel | null) => void;
  /**
   * When true, the collapsed card can render a read-only summary of the saved
   * fields (adds the eye toggle to the header). Defaults to on.
   */
  readonly showSummary?: boolean;
  /**
   * When there is no saved location yet, default the location picker to "Other" (revealing the
   * free-text box) instead of the blank/unselected state. Used by the pedigree panel.
   */
  readonly defaultLocationOther?: boolean;
}

interface ChartDraft {
  readonly date: string | null;
  /** One of {@link STATIC_LOCATIONS} or `''` when nothing is selected yet. */
  readonly locationKind: string;
  /** Only populated when `locationKind` is {@link OTHER_LOCATION}. */
  readonly locationFreeText: string;
  readonly staffMember: UserPickerOption | null;
  readonly notes: string;
}

const ANCHORS: Record<PhysicalChartParentType, { idPrefix: string; anchorId: string }> = {
  PERSON: { idPrefix: 'demographics', anchorId: 'physical-chart' },
  FAMILY: { idPrefix: 'family', anchorId: 'family-physical-chart' },
};

export function PhysicalChartCardShared({
  parentType,
  title,
  chart: chartInput,
  saving,
  onSave,
  showSummary = true,
  defaultLocationOther = false,
}: PhysicalChartCardSharedProps): ReactElement {
  const { t } = useTranslation();
  const chart = chartInput ?? EMPTY_CHART;

  // Resolve the saved staff GUID to a display name (`GET /staff/{uuid}`, cached
  // per guid). Lookup in flight → null (blank label); lookup failed → "Unknown user".
  // Never the raw guid.
  const savedStaffGuid = chart.staffMemberUserGuid;
  const resolvedNames = useResolveUserDisplayNames(savedStaffGuid !== null ? [savedStaffGuid] : []);
  const resolvedStaffName =
    savedStaffGuid !== null
      ? resolvedNameLabel(resolvedNames.get(savedStaffGuid), t('patient.careTeam.unknownUser'))
      : null;

  const initial = useMemo<ChartDraft>(
    () => deriveDraft(chart, resolvedStaffName, defaultLocationOther),
    [chart, resolvedStaffName, defaultLocationOther],
  );

  const [userQuery, setUserQuery] = useState('');
  const userSearch = useUserSearch(userQuery);

  // A draft maps to the saved shape non-trivially (locationKind / freeText / staff → location /
  // staffGuid), and two different drafts can map to the SAME saved chart — so dirtiness compares the
  // derived charts, not the raw drafts (hence the bespoke isEqual below).
  const toNext = (d: ChartDraft): PhysicalChartViewModel | null => {
    const location =
      d.locationKind === OTHER_LOCATION ? trimToNull(d.locationFreeText) : trimToNull(d.locationKind);
    const staff = d.locationKind === STAFF_LOCATION ? d.staffMember : null;
    const notes = trimToNull(d.notes);
    // `DateInput` emits `YYYY` / `YYYY-MM` mid-entry. The wire field is `format: date`, so a
    // partial value fails validation and takes the whole save down with it — including the
    // location and notes typed alongside it. Treat an incomplete date as "not set yet"; the
    // draft keeps the raw text so the field still shows what the user typed.
    const date = isFullIsoDate(d.date) ? d.date : null;
    if (date === null && location === null && staff === null && notes === null) return null;
    return {
      id: chart.id,
      date,
      location,
      staffMemberUserGuid: staff?.guid ?? null,
      notes,
    };
  };

  // PTV2-1327: the primitive owns the draft (reseed-safety, auto-registration). `changedFields`
  // shows only field NAMES in the unsaved-changes prompt.
  //
  // PTV2-1516: autosave is OFF here, so the card commits only on an explicit Save — matching the
  // other record surfaces (contacts, addresses, identifiers, allergies, …) rather than the
  // Google-docs behaviour. `autosave: false` also means the global autosave toggle does not apply
  // to this card: it always shows Save / Discard while dirty, and leaving the section prompts
  // instead of silently flushing.
  const form = useEditableDraft<ChartDraft>({
    initial,
    label: 'Physical chart',
    autosave: false,
    isEqual: (a, b) => sameChart(toNext(a) ?? EMPTY_CHART, toNext(b)),
    changedFields: (d) => {
      const n = toNext(d);
      const names: string[] = [];
      if ((n?.date ?? null) !== chart.date) names.push(t('patient.demographics.physicalChartDate'));
      if ((n?.location ?? null) !== chart.location) {
        names.push(t('patient.demographics.physicalChartLocation'));
      }
      if ((n?.staffMemberUserGuid ?? null) !== chart.staffMemberUserGuid) {
        names.push(t('patient.demographics.physicalChartStaff'));
      }
      if ((n?.notes ?? null) !== chart.notes) names.push(t('patient.demographics.physicalChartNotes'));
      return names;
    },
    onSave: (d) => {
      onSave(toNext(d));
    },
  });
  const { draft } = form;

  const { idPrefix, anchorId } = ANCHORS[parentType];

  // Read-only collapsed summary reflects the SAVED chart (resolved staff name
  // when known), matching the other record sub-sections' eye view.
  const savedLocationDisplay =
    savedStaffGuid !== null
      ? (resolvedStaffName ?? '')
      : chart.location;
  const summaryRows: readonly CollapsibleCardSummaryRow[] = [
    {
      label: t('patient.demographics.physicalChartDate'),
      value: chart.date ?? t('patient.demographics.emptyValue'),
      filled: chart.date !== null,
    },
    {
      label: t('patient.demographics.physicalChartLocation'),
      value: savedLocationDisplay ?? t('patient.demographics.emptyValue'),
      filled: savedLocationDisplay !== null && savedLocationDisplay.length > 0,
    },
    {
      label: t('patient.demographics.physicalChartNotes'),
      value: chart.notes ?? t('patient.demographics.emptyValue'),
      filled: chart.notes !== null && chart.notes.length > 0,
    },
  ];

  return (
    <CollapsibleCard
      idPrefix={idPrefix}
      anchorId={anchorId}
      title={title}
      {...(showSummary ? { summary: summaryRows } : {})}
    >
      {/* @container so the two-up layout responds to the CARD's own width, not the viewport — in a
          narrow panel (e.g. the pedigree side panel at its 260px minimum) the fields stack one per row
          (the date input gets its own line) instead of being crammed/clipped. */}
      <div ref={form.containerProps.ref} className="@container">
      <div className="physical-chart-grid grid grid-cols-1 @md:grid-cols-2 gap-x-5 gap-y-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('patient.demographics.physicalChartDate')}
          </span>
          <DateInput
            value={draft.date ?? ''}
            onChange={(value) => {
              form.setDraft((d) => ({ ...d, date: value.length > 0 ? value : null }));
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">
            {t('patient.demographics.physicalChartLocation')}
          </span>
          <Select
            value={draft.locationKind}
            className="h-9 text-sm"
            onChange={(event) => {
              const value = event.target.value;
              form.setDraft((d) => ({
                ...d,
                locationKind: value,
                // Reset linked inputs when the kind flips so a stale staff
                // member doesn't ride along after switching to "Front Desk".
                staffMember: value === STAFF_LOCATION ? d.staffMember : null,
                locationFreeText: value === OTHER_LOCATION ? d.locationFreeText : '',
              }));
            }}
          >
            <option value="">{t('patient.demographics.emptyValue')}</option>
            {STATIC_LOCATIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </label>
        {draft.locationKind === OTHER_LOCATION && (
          <label className="flex flex-col gap-1 @md:col-span-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t('patient.demographics.physicalChartLocationCustom')}
            </span>
            <Input
              value={draft.locationFreeText}
              onChange={(event) => {
                form.setDraft((d) => ({ ...d, locationFreeText: event.target.value }));
              }}
              className="text-sm"
            />
          </label>
        )}
        {draft.locationKind === STAFF_LOCATION && (
          // Not a <label> — UserPicker renders a <button> per option, and a
          // surrounding label would forward option clicks back to the input
          // and swallow the selection (React + native label semantics).
          <div className="flex flex-col gap-1 @md:col-span-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t('patient.demographics.physicalChartStaff')}
            </span>
            <UserPicker
              // The draft doesn't reseed when only the label changes (`sameChart` compares
              // guids), so label the saved staff member from the live lookup.
              value={
                draft.staffMember !== null && draft.staffMember.guid === savedStaffGuid
                  ? { ...draft.staffMember, fullName: resolvedStaffName ?? '' }
                  : draft.staffMember
              }
              onChange={(nextStaff) => {
                form.setDraft((d) => ({ ...d, staffMember: nextStaff }));
              }}
              options={userSearch.results}
              isLoading={userSearch.isLoading}
              query={userQuery}
              onQueryChange={setUserQuery}
              placeholder={t('patient.demographics.physicalChartStaffPlaceholder')}
              ariaLabel={t('patient.demographics.physicalChartStaff')}
            />
          </div>
        )}
        <label className="flex flex-col gap-1 @md:col-span-2">
          <span className="text-xs font-medium text-muted-foreground">
            {t('patient.demographics.physicalChartNotes')}
          </span>
          <Textarea
            value={draft.notes}
            rows={3}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, notes: event.target.value }));
            }}
            className="text-sm"
          />
        </label>
      </div>
      </div>
      <DraftFooter form={form} label="Physical chart" saving={saving} />
    </CollapsibleCard>
  );
}

function deriveDraft(
  chart: PhysicalChartViewModel,
  resolvedStaffName: string | null,
  defaultLocationOther = false,
): ChartDraft {
  const location = chart.location ?? '';
  const staffGuid = chart.staffMemberUserGuid;
  const kind = staffGuid !== null
    ? STAFF_LOCATION
    : STATIC_LOCATIONS.includes(location)
      ? location
      : location.length > 0
        ? OTHER_LOCATION
        // No saved location: default to "Other" in the pedigree panel, else leave unselected.
        : defaultLocationOther
          ? OTHER_LOCATION
          : '';
  return {
    date: chart.date,
    locationKind: kind,
    locationFreeText: kind === OTHER_LOCATION ? location : '',
    // The VM only carries the staff GUID. The picker relabels the saved staff
    // member from the live lookup (blank while pending, never the GUID); the guid
    // stays on the option so dirtiness compares identities, not labels.
    staffMember:
      staffGuid !== null
        ? { guid: staffGuid, fullName: resolvedStaffName ?? '' }
        : null,
    notes: chart.notes ?? '',
  };
}

function sameChart(
  a: PhysicalChartViewModel,
  b: PhysicalChartViewModel | null,
): boolean {
  if (b === null) {
    return (
      a.date === null &&
      (a.location === null || a.location.length === 0) &&
      a.staffMemberUserGuid === null &&
      (a.notes === null || a.notes.length === 0)
    );
  }
  return (
    a.date === b.date &&
    a.location === b.location &&
    a.staffMemberUserGuid === b.staffMemberUserGuid &&
    a.notes === b.notes
  );
}

function trimToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
